import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadContentOwnershipContributors } from '@confluence/content-ownership/entry-points/preloadContentOwnershipContributors.ts';
import { preloadFolderData } from '@confluence/folder/entry-points/preloadFolderData.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadSmartFolderData } from '@confluence/smart-folder/entry-points/preloadSmartFolderData.ts';

export const preloadFolderRoute = async (match: RouteMatch): Promise<void | any[] | Error[]> => {
	const { isLicensed, isRovoEnabled, cloudId } = getPreloaderFnContext();
	const { contentId, spaceKey } = match.params;

	const tasks: Promise<any>[] = [];

	if (contentId) {
		// Core folder data: the active overview content query (legacy or dual-stream,
		// depending on the cc_folder_view_type_filtered_children experiment),
		// the children-content query, and the folder header title.
		// cloudId + spaceKey are required by the dual-stream content query.
		tasks.push(preloadFolderData(contentId, { cloudId, spaceKey }));

		// Fetch content status/redirect — required by FolderPrerequisites
		tasks.push(preloadContentPrerequisites(contentId, spaceKey));

		// Preload the sticky ContentTypesHeader shown at the top of FolderOverview
		tasks.push(preloadContentTypesHeader(contentId));

		// Warm the ownership/contributors query consumed by BadgeStylePageOwnershipByline in FolderHeader
		tasks.push(preloadContentOwnershipContributors(contentId));

		if (spaceKey) {
			// Warm smart-folder enablement + ancestor data used to decide whether the
			// empty-state connection UX can render during SSR.
			tasks.push(preloadSmartFolderData({ contentId, spaceKey, isRovoEnabled }));
		}
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && spaceKey) {
		// Warm up sidebar navigation data during SSR (page tree, blog tree,
		// side nav, space views, shortcuts, space guest list)
		tasks.push(preloadNavigationTasks(spaceKey, contentId ?? '', isLicensed, false));
	}

	return waitForAllReturnWhenError(tasks);
};
